import { Accordion } from "@bcc/components/ui/accordion";
import type { FormProduct, Product, ProductConnection, SelectOption, Variant } from "@shared/types";
import { useCallback, useRef } from "react";
import { BagSizeRow } from "./bag-size-row";
import { sanitizeAeroPressGrind, setInitialPreConnections, setPreConnections } from "./product-sync-modal.connection.utils";
import type {
  ConnectedVariantsInfo,
  GetRemoteFormVariantData,
  PreConnection,
  ProductSyncOption,
  VariantPreConnections,
} from "./product-sync-modal.types";
import { setProductSyncParentChildDisplayTestId } from "./product-sync-modal.utils";

export interface ProductSyncParentChildDisplayProps {
  localProduct: Product;
  noAvailablePublishedProducts: string;
  grinds: SelectOption[];
  options: SelectOption[];
  handleSelect: (variantPreConnections: VariantPreConnections) => void;
  onClear: (targetVariantIds: string[]) => void;
  connections?: ProductConnection[];
  getRemoteFormProduct: (targetVariantId: string) => FormProduct | undefined;
}

export function ProductSyncParentChildDisplay({
  localProduct,
  handleSelect,
  options,
  noAvailablePublishedProducts,
  grinds,
  connections,
  onClear,
  getRemoteFormProduct,
}: Readonly<ProductSyncParentChildDisplayProps>) {
  const { variants: localVariants } = localProduct;

  const variantPreConnectionsRef = useRef<VariantPreConnections | undefined>(setInitialPreConnections(localVariants));

  const updateVariantPreConnections = useCallback(
    (prev: VariantPreConnections | undefined, sourceObjectId: string, targetVariantIds: string[]) => {
      if (!prev?.[sourceObjectId]) {
        return prev;
      }
      const updatedConnections = prev[sourceObjectId].filter((preConnection) => !targetVariantIds.includes(preConnection.targetVariantId));
      const newConnections = { ...prev };
      if (updatedConnections.length === 0) {
        delete newConnections[sourceObjectId];
      } else {
        newConnections[sourceObjectId] = updatedConnections;
      }
      return newConnections;
    },
    []
  );

  const handleClear = useCallback(
    (sourceObjectId: string) => (targetVariantIds: string[]) => {
      variantPreConnectionsRef.current = updateVariantPreConnections(variantPreConnectionsRef.current, sourceObjectId, targetVariantIds);
      onClear(targetVariantIds);
    },
    [onClear, updateVariantPreConnections]
  );

  const handleGetRemoteFormVariantAndProductData = useCallback(
    (targetVariantId: string): GetRemoteFormVariantData | undefined => {
      const remoteFormProduct = getRemoteFormProduct(targetVariantId);
      if (!remoteFormProduct) {
        return undefined;
      }
      const remoteVariant = remoteFormProduct.variants?.find((variant) => variant.id === targetVariantId);
      return {
        remoteFormVariant: remoteVariant,
        remoteProductTitle: remoteFormProduct.title,
      };
    },
    [getRemoteFormProduct]
  );

  const updateVariantConnection = useCallback(
    (
      prev: VariantPreConnections | undefined,
      sourceObjectId: string,
      targetVariantId: string,
      newPreConnections: PreConnection
    ): VariantPreConnections => {
      const currentConnections = prev?.[sourceObjectId] ?? [];
      const existingConnectionIndex = currentConnections.findIndex((connection) => connection.targetVariantId === targetVariantId);
      const updatedConnections =
        existingConnectionIndex >= 0
          ? currentConnections.map((connection, index) => {
              return index === existingConnectionIndex ? newPreConnections : connection;
            })
          : [...currentConnections, newPreConnections];

      const updatedVariantConnections = {
        ...prev,
        [sourceObjectId]: updatedConnections,
      };
      handleSelect(updatedVariantConnections);
      return updatedVariantConnections;
    },
    [handleSelect]
  );

  const selectVariant = useCallback(
    (sourceObjectId: string, targetVariantId: string, grindType: string[]) => {
      const updatedGrindType = grindType.length > 0 ? grindType : [];
      const newPreConnections = setPreConnections(
        sourceObjectId,
        targetVariantId,
        updatedGrindType,
        localVariants as Variant[],
        getRemoteFormProduct
      );

      if (!newPreConnections) {
        return;
      }

      variantPreConnectionsRef.current = updateVariantConnection(
        variantPreConnectionsRef.current,
        sourceObjectId,
        targetVariantId,
        newPreConnections
      );
    },
    [getRemoteFormProduct, localVariants, updateVariantConnection]
  );

  const handleSelectSingle = useCallback(
    (sourceObjectId: string) =>
      (targetVariantId: string) =>
      (grindType: string[] = []) =>
        selectVariant(sourceObjectId, targetVariantId, grindType),
    [selectVariant]
  );

  const getTitleByVariantId = useCallback(
    (targetVariantId: string) => {
      return getRemoteFormProduct(targetVariantId)?.variants?.find((variant) => variant.id === targetVariantId)?.title;
    },
    [getRemoteFormProduct]
  );

  const getConnectedVariants = useCallback(
    (localVariant: Variant): ConnectedVariantsInfo[] => {
      const connectedVariantsInfo = connections
        ?.filter((connection) => connection.sourceObjectId === localVariant.id)
        .map((connectedVariant) => {
          return {
            variantId: connectedVariant.targetVariantId,
            grind: sanitizeAeroPressGrind(connectedVariant.grindType),
          };
        });
      const groupedByVariantId = (connectedVariantsInfo ?? []).reduce(
        (acc, item) => {
          if (acc[item.variantId]) {
            acc[item.variantId]?.grinds.push(item.grind);
          } else {
            acc[item.variantId] = {
              id: item.variantId,
              grinds: [item.grind],
              title:
                getTitleByVariantId(item.variantId) ??
                connections?.find((connection) => connection.targetVariantId === item.variantId)?.identifier ??
                "Unavailable Variant",
            };
          }
          return acc;
        },
        {} as Record<string, ConnectedVariantsInfo>
      );
      return Object.values(groupedByVariantId);
    },
    [connections, getTitleByVariantId]
  );

  return (
    <div
      className="rounded-xl border bg-secondary p-4 shadow-sm space-y-3"
      data-testid={setProductSyncParentChildDisplayTestId(localProduct.id)}
    >
      <h3 className="text-lg font-semibold">{localProduct.title}</h3>
      <Accordion type="multiple">
        {localVariants
          ?.filter((localVariant) => localVariant.bagSize)
          .map((localVariant) => {
            return (
              <BagSizeRow
                key={localVariant.id}
                localVariant={localVariant}
                options={options as ProductSyncOption[]}
                grinds={grinds as ProductSyncOption[]}
                bagSize={localVariant.bagSize as string}
                onVariantSelect={handleSelectSingle}
                noAvailablePublishedProducts={noAvailablePublishedProducts}
                handleGetRemoteFormVariant={handleGetRemoteFormVariantAndProductData}
                connectedVariantsInfo={getConnectedVariants(localVariant)}
                handleClear={handleClear(localVariant.id)}
              />
            );
          })}
      </Accordion>
    </div>
  );
}
